import {Button, Input, ToastType} from '@sportcast/ui-react';
import i18n from 'i18next';
import {useEffect, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {v4} from 'uuid';
import {useAppDispatch} from '../../redux/hooks';
import {updatePassword} from '../../fetch/accountApi';
import {hideLoader} from '../../redux/action/loaderAction';
import {setMessage} from '../../redux/action/messageAction';
import PasswordStrengthIndicator from '../password/PasswordStrengthIndicator';

type ChangePasswordDialogProps = {
    show: boolean,
    handleClose: () => void
}

const ChangePasswordDialog = (props: ChangePasswordDialogProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
    const [isOldPasswordValid, setIsOldPasswordValid] = useState<boolean>(true);
    const [password, setPassword] = useState<string>('');
    const [passwordRepetition, setPasswordRepetition] = useState<string>('');
    const [oldPassword, setOldPassword] = useState<string>('');
    const [showValidation, setShowValidation] = useState<boolean>(false);

    useEffect(() => {
        setPassword('');
        setPasswordRepetition('');
        setShowValidation(false);
    }, [props.show]);

    const save = () => {
        setShowValidation(true);

        if (isPasswordValid) {
            updatePassword(password, oldPassword, dispatch).then(response => {
                if (response.success) {
                    dispatch(setMessage({
                        i18nMessage: 'message.SAVE_SUCCESS',
                        type: ToastType.SUCCESS,
                        messageId: v4()
                    }));
                    props.handleClose();
                    setIsOldPasswordValid(true);
                }
            }).catch(error => {
                if (error.status === 417) {
                    dispatch(setMessage({
                        message: i18n.t('error.WRONG_PASSWORD'),
                        type: ToastType.ERROR,
                        messageId: v4(),
                        autoClose: true
                    }));
                    dispatch(hideLoader());
                    setIsOldPasswordValid(false);
                }
            });
        }
    };

    const getErrorMessage = () => {
        if (!showValidation || isPasswordValid) {
            return '';
        }
        if (password !== passwordRepetition) {
            return t('validation.PASSWORD_MISMATCH');
        }
        return t('validation.PASSWORD_STRENGTH');
    };

    return (
        <Modal show={props.show}
               animation={false}
               scrollable
               backdrop="static"
               onHide={props.handleClose}>
            <Modal.Header closeButton={true}>
                <h5>
                    {t('passwordDialog.TITLE')}
                </h5>
            </Modal.Header>
            <Modal.Body>
                <div className="my-3">
                    <PasswordStrengthIndicator
                        onValidate={setIsPasswordValid}
                        password={password}
                        passwordRepetition={passwordRepetition}/>
                </div>
                <input type="text" autoComplete="username" className="d-none"/>
                <input type="password" autoComplete="current-password" className="d-none"/>

                <Input
                    id="password"
                    className="mb-3"
                    type="password"
                    label={t('forgotPassword.PASSWORD')}
                    required={true}
                    onChange={(_key, value) => setPassword(String(value))}
                    value={password}
                />

                <Input
                    id="password-repetition"
                    type="password"
                    label={t('forgotPassword.PASSWORD_REPETITION')}
                    required={true}
                    onChange={(_key, value) => setPasswordRepetition(String(value))}
                    value={passwordRepetition}
                    errorMessage={getErrorMessage()}
                />

                <Input
                    id="old-password"
                    type="password"
                    label={t('forgotPassword.OLD_PASSWORD')}
                    required={true}
                    onChange={(_key, value) => setOldPassword(String(value))}
                    value={oldPassword}
                    errorMessage={(showValidation && (!oldPassword || !isOldPasswordValid)) ? t('error.REQUIRED') : undefined}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline"
                    label={t('button.CANCEL')}
                    onClick={props.handleClose}
                />
                <Button
                    label={t('button.SAVE')}
                    onClick={save}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default ChangePasswordDialog;
